import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  COMMITMENTS, CAPITAL_ACCOUNTS, FUND_TRANSACTIONS, INVESTMENTS, WIRE_INSTRUCTIONS,
  fmt, fmtPct, commitmentTypeLabel
} from "../../data/portalDemo.js";
import { fetchDocuments, downloadUrl } from "../../lib/portalApi.js";

const TABS = ["Overview", "Documents", "Investments", "Capital accounts", "Fund transactions", "Wire instructions"];

export default function PortalFundDetail() {
  const { slug } = useParams();
  const fund = COMMITMENTS.find(c => c.slug === slug);
  const [tab, setTab] = useState("Overview");

  if (!fund) {
    return (
      <div className="portal-page">
        <p>Investment not found.</p>
        <Link className="portal-link" to="/portal">← Back to portfolio</Link>
      </div>
    );
  }

  const ccy = fund.currency;
  const isWholeLoan = fund.type === "uk_whole_loan_bnote" || fund.type === "hospitality" || fund.type === "india_ncd_direct";
  const isPooled     = fund.type === "uk_warehouse_fl" || fund.type === "india_lux_feeder";

  return (
    <div className="portal-page">
      <div className="portal-breadcrumb">
        <Link to="/portal" className="portal-link">Portfolio</Link> / <span>Investment</span>
      </div>
      <h1 className="portal-h1">{fund.fund}</h1>
      <div className="muted">
        <span className="type-pill">{commitmentTypeLabel(fund.type)}</span>
        {" · "}{fund.vehicle} · Vintage {fund.vintage} · {fund.domicile}
      </div>

      <div className="portal-tabs">
        {TABS.map(t => (
          <button key={t}
                  className={"portal-tab" + (tab === t ? " active" : "")}
                  onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview"            && <OverviewTab fund={fund} ccy={ccy} isWholeLoan={isWholeLoan} />}
      {tab === "Documents"           && <DocsTab slug={slug} />}
      {tab === "Investments"         && <InvestmentsTab slug={slug} ccy={ccy} isWholeLoan={isWholeLoan} />}
      {tab === "Capital accounts"    && <CapitalAccountsTab slug={slug} ccy={ccy} isWholeLoan={isWholeLoan} />}
      {tab === "Fund transactions"   && <FundTransactionsTab slug={slug} ccy={ccy} />}
      {tab === "Wire instructions"   && <WireTab />}
    </div>
  );
}

function OverviewTab({ fund, ccy, isWholeLoan }) {
  const pct = fund.committed > 0 ? Math.round((fund.contributedPaid / fund.committed) * 100) : 0;
  const commitLabel = isWholeLoan ? "Whitelist commitment" : "Fund commitment";
  return (
    <>
      <section className="portal-card">
        <div className="portal-card-head">
          <h2 className="portal-h2">{commitLabel}</h2>
          <span className="muted">Primary contact: <a className="portal-link" href={`mailto:${fund.primaryContact}`}>{fund.primaryContact}</a></span>
        </div>
        <div className="commit-bar">
          <div className="commit-fill" style={{ width: pct + "%" }} />
        </div>
        <div className="commit-legend">
          <span><span className="dot dot-fill"></span> {isWholeLoan ? "Cumulative deployed" : "Contributed Paid"} {fmt(fund.contributedPaid, ccy)}</span>
          <span><span className="dot dot-track"></span> {isWholeLoan ? "Whitelist commitment" : "Committed"} {fmt(fund.committed, ccy)}</span>
        </div>
        <div className="kpi-grid kpi-grid-fund">
          <Kpi label="Vintage"        value={fund.vintage} />
          <Kpi label={isWholeLoan ? "Whitelist commitment" : "Committed"} value={fmt(fund.committed, ccy)} />
          <Kpi label={isWholeLoan ? "Deployed"  : "Called Capital"}  value={fmt(fund.called, ccy)} />
          <Kpi label={isWholeLoan ? "Outstanding" : "Contributed Paid"} value={`${fmt(fund.contributedPaid, ccy)} (${pct}%)`} highlight />
          {!isWholeLoan && <Kpi label="Contributed GAAP" value={fmt(fund.contributedGAAP, ccy)} />}
          <Kpi label="Unfunded"       value={fmt(fund.committed - fund.called, ccy)} />
          <Kpi label={isWholeLoan ? "Returned" : "Distributed"} value={fmt(fund.distributed, ccy)} />
          <Kpi label="Mark / NAV"     value={fmt(fund.nav, ccy)} highlight />
          <Kpi label="Net IRR"        value={fmtPct(fund.netIRR)} />
          <Kpi label="TVPI"           value={fund.tvpi.toFixed(2) + "x"} />
        </div>
      </section>

      <section className="portal-card">
        <h2 className="portal-h2">Vehicle profile</h2>
        <table className="portal-keyval">
          <tbody>
            <tr><th>Structure</th><td>{commitmentTypeLabel(fund.type)}</td></tr>
            <tr><th>Strategy</th><td>{fund.strategy}</td></tr>
            <tr><th>Sector</th><td>{fund.sector}</td></tr>
            <tr><th>Target net return</th><td>{fund.targetNetReturn}</td></tr>
            <tr><th>Fee structure</th><td>{fund.fees}</td></tr>
            <tr><th>Vehicle</th><td>{fund.vehicle}</td></tr>
            <tr><th>Domicile</th><td>{fund.domicile}</td></tr>
            <tr><th>Fund administrator</th><td>{fund.fundAdmin}</td></tr>
            <tr><th>Auditor</th><td>{fund.auditor}</td></tr>
            {fund.banker && <tr><th>Banker</th><td>{fund.banker}</td></tr>}
            {fund.legal && <tr><th>Legal counsel</th><td>{fund.legal}</td></tr>}
            {fund.structuring && <tr><th>Structuring</th><td>{fund.structuring}</td></tr>}
          </tbody>
        </table>
      </section>
    </>
  );
}

function DocsTab({ slug }) {
  const [allDocs, setAllDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchDocuments()
      .then(d => { if (alive) setAllDocs(d); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  if (error) {
    return <p className="muted small" style={{ color: "var(--portal-neg)" }}>{error}</p>;
  }
  if (!allDocs) {
    return <p className="muted small">Loading documents from Box…</p>;
  }
  const docs = allDocs.filter(d => d.fund === slug);
  if (!docs.length) return <p className="muted">No documents for this investment yet.</p>;

  return (
    <section className="portal-card">
      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr><th>Document</th><th>Type</th><th>Date</th><th></th></tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id}>
                <td>{d.title}</td>
                <td className="muted">{d.type}</td>
                <td className="muted">{d.date}</td>
                <td><a className="portal-rowlink" href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer">Open</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InvestmentsTab({ slug, ccy, isWholeLoan }) {
  const rows = INVESTMENTS[slug] || [];
  if (!rows.length) return <p className="muted">No investments disclosed for this period.</p>;

  if (isWholeLoan) {
    // Whole-loan facility — LP's direct deal-by-deal positions.
    return (
      <section className="portal-card">
        <p className="muted">Direct LP positions, deal by deal. Each position is a Daksfirst-originated UK bridging loan that you elected into after pre-IC memo. Servicing fee 1% on outstanding; arrangement fee 0.5% per deal at funding.</p>
        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Deal ID</th><th>Borrower</th><th>Sector</th>
                <th className="num">Your commitment</th>
                <th className="num">Outstanding</th>
                <th className="num">LTV</th>
                <th>Coupon</th>
                <th className="num">Earned YTD</th>
                <th>Maturity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dealId}>
                  <td className="muted">{r.dealId}</td>
                  <td>{r.borrower}</td>
                  <td className="muted">{r.sector}</td>
                  <td className="num">{fmt(r.lpCommitment, ccy)}</td>
                  <td className="num">{fmt(r.principalOutstanding, ccy)}</td>
                  <td className="num">{r.ltv}</td>
                  <td className="muted">{r.rate}</td>
                  <td className="num pos">{fmt(r.couponEarnedYTD, ccy)}</td>
                  <td className="muted">{r.maturity}</td>
                  <td><span className={"pill " + (/Performing/.test(r.status) ? "pill-good" : "pill-warn")}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  // Warehouse / pooled fund — look-through view of underlying loans (no LP-level data here)
  return (
    <section className="portal-card">
      <p className="muted">Look-through view of the fund&rsquo;s underlying loan portfolio. Pro-rata exposure based on your commitment share. Daksfirst-specific — most LP portals don&rsquo;t disclose this.</p>
      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr>
              <th>Borrower</th><th>Sector</th><th>Jurisdiction</th>
              <th className="num">LTV</th><th className="num">DSCR</th>
              <th>Coupon</th><th>Maturity</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.borrower}</td>
                <td className="muted">{r.sector}</td>
                <td className="muted">{r.jurisdiction}</td>
                <td className="num">{r.ltv}</td>
                <td className="num">{r.dscr}</td>
                <td className="muted">{r.rate}</td>
                <td className="muted">{r.maturity}</td>
                <td><span className="pill pill-good">{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CapitalAccountsTab({ slug, ccy, isWholeLoan }) {
  const data = CAPITAL_ACCOUNTS[slug];
  if (!data) return <p className="muted">No capital account data.</p>;
  const summaryTitle = isWholeLoan ? "Facility roll-forward" : "Capital account summary";
  const commitTitle  = isWholeLoan ? "Whitelist & deployment" : "Commitment summary";
  const unfundedLabel = isWholeLoan ? "Available to deploy" : "Unfunded commitment";
  return (
    <section className="portal-card">
      <div className="portal-card-head">
        <h2 className="portal-h2">{isWholeLoan ? "Facility activity" : "Capital accounts"}</h2>
        <span className="muted">Period: {data.period}</span>
      </div>
      <p className="muted small">All amounts in {ccy}.</p>
      <h3 className="portal-h3">{summaryTitle}</h3>
      <table className="portal-table portal-table-2col">
        <thead>
          <tr><th></th><th className="num">Statement period</th><th className="num">Inception to date</th></tr>
        </thead>
        <tbody>
          {data.summary.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              <td className={"num " + (r.period < 0 ? "neg" : r.period > 0 ? "pos" : "")}>{fmt(r.period, ccy)}</td>
              <td className={"num " + (r.itd    < 0 ? "neg" : r.itd    > 0 ? "pos" : "")}>{fmt(r.itd, ccy)}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Ending balance</td>
            <td className="num strong">{fmt(data.endingBalance.period, ccy)}</td>
            <td className="num strong">{fmt(data.endingBalance.itd,    ccy)}</td>
          </tr>
        </tbody>
      </table>

      <h3 className="portal-h3">{commitTitle}</h3>
      <table className="portal-table portal-table-2col">
        <thead>
          <tr><th></th><th className="num">Statement period</th><th className="num">Inception to date</th></tr>
        </thead>
        <tbody>
          {data.commitment.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              <td className="num">{fmt(r.period, ccy)}</td>
              <td className="num">{fmt(r.itd, ccy)}</td>
            </tr>
          ))}
          <tr className="total-row">
            <td>{unfundedLabel}</td>
            <td className="num strong">{fmt(data.unfunded.period, ccy)}</td>
            <td className="num strong">{fmt(data.unfunded.itd,    ccy)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function FundTransactionsTab({ slug, ccy }) {
  const rows = FUND_TRANSACTIONS[slug] || [];
  return (
    <section className="portal-card">
      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr><th>Date</th><th>Type</th><th className="num">Amount</th><th>Notes</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="muted">{r.date}</td>
                <td>{r.type}</td>
                <td className={"num " + (r.amount < 0 ? "neg" : r.amount > 0 ? "pos" : "")}>{fmt(r.amount, ccy)}</td>
                <td className="muted">{r.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function WireTab() {
  return (
    <section className="portal-card">
      <h2 className="portal-h2">Wire instructions</h2>
      {WIRE_INSTRUCTIONS.status === "not-set" ? (
        <div className="empty-state">
          <p className="muted">No wire instructions on file. Distributions cannot be sent until wire instructions are recorded.</p>
          <button className="btn btn-primary">Add wire instructions</button>
        </div>
      ) : (
        <table className="portal-keyval">
          <tbody>
            <tr><th>Beneficiary</th><td>{WIRE_INSTRUCTIONS.beneficiary}</td></tr>
            <tr><th>Bank</th><td>{WIRE_INSTRUCTIONS.bankName}</td></tr>
            <tr><th>Bank address</th><td>{WIRE_INSTRUCTIONS.bankAddress}</td></tr>
            <tr><th>SWIFT/BIC</th><td>{WIRE_INSTRUCTIONS.swift}</td></tr>
            <tr><th>IBAN</th><td>{WIRE_INSTRUCTIONS.iban}</td></tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

function Kpi({ label, value, highlight }) {
  return (
    <div className={"kpi" + (highlight ? " kpi-strong" : "")}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
